import { withDependencies } from '@wix/thunderbolt-ioc'
import {
	Props,
	IPropsStore,
	Structure,
	IStructureStore,
	IStylesStore,
	Store,
	StylesStoreSymbol,
	BatchingStrategy,
	BatchingStrategySymbol,
} from '@wix/thunderbolt-symbols'
import _ from 'lodash'
import { DevToolsSymbol } from './symbols'
import { IDevtoolsConnector } from './types'

interface Stores {
	props: IPropsStore
	structure: IStructureStore
	styles: IStylesStore
}

type State = { [store in keyof Stores]: Stores[store] extends Store<infer S> ? S : never } & {
	carmi?: Record<string, any>
}

export const DevtoolsFactory = withDependencies(
	[Structure, Props, StylesStoreSymbol, BatchingStrategySymbol],
	(
		structure: IStructureStore,
		props: IPropsStore,
		styles: IStylesStore,
		batchingStrategy: BatchingStrategy
	): IDevtoolsConnector => ({
		connect: (carmiStore) => {
			const devToolsEnhancer = window.__REDUX_DEVTOOLS_EXTENSION__?.connect
			if (!devToolsEnhancer || window[DevToolsSymbol]) {
				return
			}
			window[DevToolsSymbol] = true

			const prevSnapshot: State = {
				props: props.getEntireStore(),
				structure: structure.getEntireStore(),
				styles: styles.getEntireStore(),
				...(carmiStore ? { carmi: carmiStore.getEntireStore() } : {}),
			}

			// Update the state only from the store that was triggering the change
			const snapshot = (updatedStoreValue?: Partial<State>): State => {
				Object.assign(prevSnapshot, updatedStoreValue)
				return prevSnapshot
			}

			const updateStores = (storesSnapshot: State) => {
				props.update(storesSnapshot.props)
				structure.update(storesSnapshot.structure)
				styles.update(storesSnapshot.styles)
			}
			let isTimeTraveling = false
			const batchData = {
				partialUpdates: [] as Array<Record<string, any>>,
				name: undefined as keyof State | undefined,
			}
			const devtools = devToolsEnhancer<State>({ name: 'thunderbolt', shouldRecordChanges: true, maxAge: 50 })
			const subscribe = (name: keyof State) => async (partialUpdate: any) => {
				const updateSize = Object.keys(partialUpdate).length
				if (!isTimeTraveling && updateSize > 0) {
					snapshot({
						[name]: { ...prevSnapshot[name], ...partialUpdate },
					})

					const { partialUpdates } = batchData
					partialUpdates.push({ [name]: partialUpdate })
					if (partialUpdates.length === 1) {
						batchData.name = name
						await window.reactAndReactDOMLoaded
						batchingStrategy.batch(() => {
							const action =
								partialUpdates.length === 1
									? {
											type: `UPDATE_${_.toUpper(batchData.name)}`,
											update: partialUpdates[0][name],
									  }
									: {
											type: 'UPDATE_STORES',
											updates: partialUpdates,
									  }
							devtools.send(action, snapshot())
							batchData.partialUpdates = []
						})
					}
				}
			}

			props.subscribeToChanges(subscribe('props'))
			structure.subscribeToChanges(subscribe('structure'))
			styles.subscribeToChanges(subscribe('styles'))
			const carmiSubscribe = subscribe('carmi')
			carmiStore?.subscribeToChanges(() => {
				carmiSubscribe(carmiStore.getEntireStore())
			})

			// This needs some work to support time travel and other devtools features
			devtools.subscribe((message) => {
				if (message.type === 'DISPATCH' && message.state) {
					if (message.payload?.type === 'JUMP_TO_ACTION' || message.payload?.type === 'JUMP_TO_STATE') {
						isTimeTraveling = true
						const state: State = JSON.parse(message.state)
						updateStores(state)
						isTimeTraveling = false
					}
				} else if (message.type === 'DISPATCH' && message.payload?.type === 'COMMIT') {
					devtools.init(snapshot())
				}
			})

			devtools.init(snapshot())
		},
	})
)
